import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Play, Users, X, RefreshCw, ChevronDown, ChevronUp, Bookmark, FileSpreadsheet, CheckCircle, Search, Send, Eye, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CollusionTab() {
  const [loading, setLoading] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [filtroJugador, setFiltroJugador] = useState('');
  const [filtroNivel, setFiltroNivel] = useState('');

  const [resumen, setResumen] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [totalAlertas, setTotalAlertas] = useState(0);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [teamsGrabados, setTeamsGrabados] = useState(new Set());
  const [enviandoWA, setEnviandoWA] = useState(false);
  const [fechaAnalisis, setFechaAnalisis] = useState('');
  const [fechaDatos, setFechaDatos] = useState('');
  const [importando, setImportando] = useState(false);
  const [vinculados, setVinculados] = useState([]);
  const [showVinculados, setShowVinculados] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { cargarAlertas(); }, []);
  useEffect(() => { cargarAlertas(); }, [filtroNivel, filtroJugador]);

  const cargarAlertas = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroNivel) params.append('nivel', filtroNivel);
      if (filtroJugador) params.append('jugador', filtroJugador);
      params.append('limit', '100');

      const res = await axios.get(`${API}/collusion/alertas?${params}`, { headers: authHeaders });
      setAlertas(res.data.alertas || []);
      setTotalAlertas(res.data.total || 0);
      if (res.data.resumen) setResumen(res.data.resumen);
      if (res.data.fecha_analisis) setFechaAnalisis(res.data.fecha_analisis);
      if (res.data.fecha_desde && res.data.fecha_hasta) {
        setFechaDatos(`${res.data.fecha_desde} a ${res.data.fecha_hasta}`);
      }
    } catch (error) {
      console.error('Error cargando alertas collusion:', error);
    } finally {
      setLoading(false);
    }
  };

  const ejecutarAnalisis = async () => {
    setEjecutando(true);
    try {
      const params = new URLSearchParams();
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);

      const res = await axios.post(`${API}/collusion/analizar?${params}`, {}, { headers: authHeaders });
      toast.success(`Analisis completado: ${res.data.total_alertas} teams detectados en ${res.data.duracion_segundos}s`);
      await cargarAlertas();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEjecutando(false);
    }
  };

  const toggleGrabar = (idx) => {
    setTeamsGrabados(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const getTeamsSeleccionados = () => [...teamsGrabados].map(idx => alertas[idx]).filter(Boolean);

  const exportarReporte = async () => {
    const sel = getTeamsSeleccionados();
    if (!sel.length) { toast.error('No hay teams grabados'); return; }
    try {
      const res = await axios.post(`${API}/collusion/exportar-reporte`, sel, {
        headers: authHeaders, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `collusion_reporte_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Reporte generado: ${sel.length} teams`);
      setTeamsGrabados(new Set());
      await cargarAlertas();
    } catch (error) {
      toast.error('Error al generar reporte');
    }
  };

  const enviarPorWhatsApp = async () => {
    const sel = getTeamsSeleccionados();
    if (!sel.length) { toast.error('No hay teams grabados'); return; }
    setEnviandoWA(true);
    try {
      const res = await axios.post(`${API}/collusion/enviar-reporte-whatsapp`, sel, { headers: authHeaders });
      if (res.data.success) {
        toast.success(res.data.mensaje);
        setTeamsGrabados(new Set());
        await cargarAlertas();
      } else {
        toast.error(res.data.mensaje || 'Error WhatsApp');
      }
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEnviandoWA(false);
    }
  };

  const getNivelBadge = (nivel) => {
    const styles = {
      CRITICO: 'bg-red-600 text-white animate-pulse',
      ALTO: 'bg-orange-500 text-white',
      MEDIO: 'bg-yellow-400 text-gray-900',
      BAJO: 'bg-gray-300 text-gray-700',
    };
    return styles[nivel] || 'bg-gray-200';
  };

  const importarRespuestaPPPoker = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportando(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/collusion/importar-respuesta-pppoker`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        toast.success(`${res.data.mensaje} (${res.data.confirmados} YES/ALTO, ${res.data.descartados} NO)`);
        cargarVinculados();
        cargarAlertas();
      } else {
        toast.error(res.data.mensaje || 'Error al importar');
      }
    } catch (error) {
      toast.error('Error al importar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImportando(false);
      e.target.value = '';
    }
  };

  const cargarVinculados = async () => {
    try {
      const res = await axios.get(`${API}/collusion/vinculados`, { headers: authHeaders });
      setVinculados(res.data.vinculados || []);
    } catch (e) { /* silencioso */ }
  };

  useEffect(() => { cargarVinculados(); }, []);

  const formatNum = (n) => {
    if (n === null || n === undefined) return '0';
    return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
  };

  return (
    <div className="space-y-4" data-testid="collusion-tab">
      {/* Tarjetas Resumen */}
      {resumen ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="collusion-summary">
          <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-400 rounded-xl p-4 text-center">
            <p className="text-3xl font-black text-red-600">{resumen.criticas || 0}</p>
            <p className="text-xs font-bold text-red-800 mt-1">CRITICAS</p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/30 border-2 border-orange-400 rounded-xl p-4 text-center">
            <p className="text-3xl font-black text-orange-600">{resumen.altas || 0}</p>
            <p className="text-xs font-bold text-orange-800 mt-1">ALTAS</p>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-400 rounded-xl p-4 text-center">
            <p className="text-3xl font-black text-yellow-600">{resumen.medias || 0}</p>
            <p className="text-xs font-bold text-yellow-800 mt-1">MEDIAS</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/30 border-2 border-purple-400 rounded-xl p-4 text-center">
            <p className="text-3xl font-black text-purple-600">{resumen.total || 0}</p>
            <p className="text-xs font-bold text-purple-800 mt-1">TOTAL TEAMS</p>
          </div>
          <div className="bg-indigo-50 dark:bg-indigo-900/30 border-2 border-indigo-400 rounded-xl p-4 text-center">
            <p className="text-3xl font-black text-indigo-600">{resumen.bajas || 0}</p>
            <p className="text-xs font-bold text-indigo-800 mt-1">BAJAS</p>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 font-semibold">No hay analisis de collusion previos</p>
          <p className="text-sm text-gray-400 mt-1">Ejecuta un analisis para detectar teams depredadores</p>
        </div>
      )}

      {/* Controles */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-purple-300 p-4" data-testid="collusion-controls">
        <div className="flex flex-col md:flex-row items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-bold text-purple-800 mb-2">
              Ejecutar Analisis de Collusion
            </label>
            <p className="text-xs text-purple-700 mb-3">
              Detecta jugadores que se sientan juntos sistematicamente en mesas PPSR. Default: ultimos 15 dias.
            </p>
            <div className="flex gap-3 flex-wrap">
              <div>
                <span className="block text-xs text-purple-700 mb-1 font-semibold">Desde</span>
                <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                  className="w-44 px-3 py-2 border-2 border-purple-300 rounded-lg bg-white text-gray-900 text-sm"
                  data-testid="collusion-fecha-desde" />
              </div>
              <div>
                <span className="block text-xs text-purple-700 mb-1 font-semibold">Hasta</span>
                <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                  className="w-44 px-3 py-2 border-2 border-purple-300 rounded-lg bg-white text-gray-900 text-sm"
                  data-testid="collusion-fecha-hasta" />
              </div>
              <div>
                <span className="block text-xs text-purple-700 mb-1 font-semibold">Investigar Jugador</span>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-purple-400" />
                  <input type="text" value={filtroJugador} onChange={(e) => setFiltroJugador(e.target.value)}
                    placeholder="ID o nombre..."
                    className="w-48 pl-9 pr-3 py-2 border-2 border-purple-300 rounded-lg bg-white text-gray-900 text-sm"
                    data-testid="collusion-filtro-jugador" />
                </div>
              </div>
            </div>
          </div>
          <button onClick={ejecutarAnalisis} disabled={ejecutando} data-testid="collusion-ejecutar-btn"
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
              ejecutando ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg'
            }`}>
            {ejecutando ? (<><RefreshCw className="w-5 h-5 animate-spin" />Analizando...</>) : (<><Play className="w-5 h-5" />Ejecutar Analisis</>)}
          </button>
        </div>
        {fechaAnalisis && (
          <div className="mt-3 text-xs text-purple-700">
            Ultimo analisis: {new Date(fechaAnalisis).toLocaleString('es-AR')} | Datos: {fechaDatos || 'N/A'}
          </div>
        )}
      </div>

      {/* Filtros por Nivel */}
      <div className="flex gap-2 flex-wrap items-center" data-testid="collusion-filtros">
        {['', 'CRITICO', 'ALTO', 'MEDIO', 'BAJO'].map(nivel => (
          <button key={nivel || 'TODOS'} onClick={() => setFiltroNivel(nivel)}
            data-testid={`collusion-filtro-${nivel || 'todos'}`}
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              filtroNivel === nivel
                ? (nivel === 'CRITICO' ? 'bg-red-600 text-white shadow-lg'
                  : nivel === 'ALTO' ? 'bg-orange-500 text-white shadow-lg'
                  : nivel === 'MEDIO' ? 'bg-yellow-400 text-gray-900 shadow-lg'
                  : nivel === 'BAJO' ? 'bg-gray-500 text-white shadow-lg'
                  : 'bg-purple-600 text-white shadow-lg')
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            {nivel || 'TODOS'}
          </button>
        ))}
        <span className="text-sm text-gray-500 ml-2">
          Mostrando {alertas.length} de {totalAlertas} teams
        </span>
        {teamsGrabados.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={exportarReporte} data-testid="collusion-exportar-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold text-sm shadow-lg">
              <FileSpreadsheet className="w-4 h-4" />
              Reportar {teamsGrabados.size} teams
            </button>
            <button onClick={enviarPorWhatsApp} disabled={enviandoWA} data-testid="collusion-whatsapp-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm shadow-lg ${
                enviandoWA ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-[#25D366] hover:bg-[#128C7E] text-white'
              }`}>
              {enviandoWA ? (<><RefreshCw className="w-4 h-4 animate-spin" />Enviando...</>) : (<><Send className="w-4 h-4" />WhatsApp</>)}
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm shadow-lg cursor-pointer" data-testid="collusion-importar-btn">
            {importando ? (<><RefreshCw className="w-4 h-4 animate-spin" />Importando...</>) : (<><Upload className="w-4 h-4" />Importar Respuesta PPPoker</>)}
            <input type="file" accept=".xlsx,.xls" onChange={importarRespuestaPPPoker} className="hidden" disabled={importando} />
          </label>
        </div>
      </div>

      {/* Vinculados confirmados - agrupados */}
      {vinculados.length > 0 && (() => {
        const grupos = {};
        vinculados.forEach(v => {
          const gk = v.grupo_key || v.jugador_id;
          if (!grupos[gk]) grupos[gk] = { team_num: v.team_num, score: v.team_score, nivel: v.team_nivel, miembros: [] };
          grupos[gk].miembros.push(v);
        });
        return (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 shadow" data-testid="collusion-vinculados">
            <button 
              onClick={() => setShowVinculados(!showVinculados)}
              className="w-full flex items-center justify-between cursor-pointer hover:bg-green-100 rounded-lg p-1 transition-colors"
            >
              <h3 className="font-bold text-green-800">Confirmados por PPPoker: {vinculados.length} jugadores en {Object.keys(grupos).length} grupos vinculados</h3>
              {showVinculados ? <ChevronUp className="w-5 h-5 text-green-600" /> : <ChevronDown className="w-5 h-5 text-green-600" />}
            </button>
            {showVinculados && <div className="space-y-3 mt-3">
              {Object.entries(grupos).map(([gk, g]) => (
                <div key={gk} className="bg-white border border-green-300 rounded-lg p-3 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">Team #{g.team_num}</span>
                    <span className="text-sm font-semibold text-gray-700">Score: {g.score}</span>
                    <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                      g.nivel === 'CRITICO' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                    }`}>{g.nivel}</span>
                    {g.miembros.length > 1 && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-bold">MISMO DISPOSITIVO ({g.miembros.length})</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {g.miembros.map((m, i) => (
                      <div key={i} className="flex items-center gap-1 bg-green-100 border border-green-300 px-3 py-1 rounded-lg text-sm">
                        <span className="font-bold text-green-800">{m.jugador_id}</span>
                        <span className="text-green-700">{m.jugador_nombre}</span>
                        <span className="text-xs text-gray-500">({m.club_nombre})</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>}
          </div>
        );
      })()}

      {/* Tabla de Teams */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 overflow-hidden" data-testid="collusion-tabla">
        {alertas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin" />Cargando...
              </div>
            ) : 'No hay alertas. Ejecuta un analisis primero.'}
          </div>
        ) : (
          <div className="space-y-2 p-3">
            {alertas.map((a, idx) => (
              <div key={a.alerta_id || idx} data-testid={`collusion-team-${idx}`}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {/* Header del team */}
                <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
                  onClick={() => setExpandedTeam(expandedTeam === idx ? null : idx)}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${getNivelBadge(a.nivel)}`}>
                      {a.nivel}
                    </span>
                    <span className="font-black text-lg text-purple-700">{a.score}</span>
                    <span className="text-sm font-semibold text-gray-700">
                      <Users className="w-4 h-4 inline mr-1" />{a.team_size} jugadores
                    </span>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-mono">
                      {a.total_mesas_compartidas} coincidencias
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                      Max: {a.max_coocurrencia_pct}%
                    </span>
                    {a.is_cross_union && (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded font-bold">
                        CROSS-UNION
                      </span>
                    )}
                    {a.ganancia_conjunta > 0 && (
                      <span className="text-xs text-green-700 font-semibold">
                        +{formatNum(a.ganancia_conjunta)} chips
                      </span>
                    )}
                    {a.ya_reportado && (
                      <span className="flex items-center gap-1 text-xs text-green-600 font-bold">
                        <CheckCircle className="w-3 h-3" /> {a.tracking}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!a.ya_reportado && (
                      <button onClick={(e) => { e.stopPropagation(); toggleGrabar(idx); }}
                        data-testid={`collusion-grabar-${idx}`}
                        className={`p-1.5 rounded-lg transition-all ${
                          teamsGrabados.has(idx)
                            ? 'bg-purple-600 text-white shadow-md'
                            : 'bg-gray-200 text-gray-500 hover:bg-purple-100'
                        }`}>
                        <Bookmark className="w-4 h-4" />
                      </button>
                    )}
                    {expandedTeam === idx ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </div>
                </div>

                {/* Detalle expandido */}
                {expandedTeam === idx && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50 dark:bg-gray-900/50 space-y-4">
                    {/* Razones */}
                    <div>
                      <p className="text-xs font-bold text-gray-600 mb-1">Razones:</p>
                      <div className="space-y-0.5">
                        {(a.razones || []).map((r, ri) => (
                          <div key={ri} className="flex items-start gap-1 text-xs">
                            <AlertTriangle className="w-3 h-3 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{r}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Miembros */}
                    <div>
                      <p className="text-xs font-bold text-gray-600 mb-2">Miembros del Team:</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-purple-600 text-white">
                              <th className="px-3 py-2 text-left">ID</th>
                              <th className="px-3 py-2 text-left">Nombre</th>
                              <th className="px-3 py-2 text-center">Union</th>
                              <th className="px-3 py-2 text-left">Club</th>
                              <th className="px-3 py-2 text-center">Mesas</th>
                              <th className="px-3 py-2 text-right">Ganancia</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(a.miembros || []).map((m, mi) => (
                              <tr key={mi} className="border-b border-gray-200 hover:bg-purple-50">
                                <td className="px-3 py-1.5 font-mono">{m.jugador_id}</td>
                                <td className="px-3 py-1.5 font-semibold">{m.jugador_nombre}</td>
                                <td className="px-3 py-1.5 text-center">
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">{m.union_id}</span>
                                </td>
                                <td className="px-3 py-1.5 text-gray-600">{m.club_nombre}</td>
                                <td className="px-3 py-1.5 text-center font-bold">{m.total_mesas}</td>
                                <td className={`px-3 py-1.5 text-right font-semibold ${m.ganancia_total < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {formatNum(m.ganancia_total)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pares de co-ocurrencia */}
                    {a.pares && a.pares.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-gray-600 mb-2">Co-ocurrencia entre pares:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {a.pares.map((p, pi) => (
                            <div key={pi} className="flex items-center justify-between bg-white border border-purple-200 rounded-lg p-2 text-xs">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-purple-800">{p.j1_id}</span>
                                <span className="text-gray-400">vs</span>
                                <span className="font-mono text-purple-800">{p.j2_id}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-bold">{p.mesas_comunes} mesas</span>
                                <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-bold">
                                  {p.pct_j1}% / {p.pct_j2}%
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
